import { Edge } from 'reactflow';
import { CustomNode } from '../types'; // Assuming CustomNode is in src/types/index.ts
import { CanvasUpdateAction } from '../../backend/src/types/ai';

/**
 * Generates a concise string summary of canvas nodes and edges.
 * This summary is intended for providing context to an LLM.
 *
 * @param nodes - An array of CustomNode objects from the Zustand store.
 * @param edges - An array of Edge objects from the Zustand store.
 * @returns A string summarizing the canvas elements.
 */
export const generateCanvasSummaryString = (
  nodes: CustomNode[],
  edges: Edge[],
): string => {
  let summary = 'Nodes:\n';
  if (nodes.length === 0) {
    summary += '- (No nodes on canvas)\n';
  } else {
    nodes.forEach(node => {
      summary += `- ID: ${node.id}, Type: ${node.data.type}, Label: ${node.data.label}\n`;
    });
  }

  summary += '\nEdges:\n';
  if (edges.length === 0) {
    summary += '- (No edges on canvas)\n';
  } else {
    edges.forEach(edge => {
      summary += `- ID: ${edge.id}, From: ${edge.source}, To: ${edge.target}`;
      if (edge.label) {
        summary += `, Label: ${edge.label}`;
      }
      summary += '\n';
    });
  }

  return summary.trim();
};

/**
 * Generates canvas actions to clear all nodes and edges from the canvas.
 * 
 * @param nodesOrSummary - Either an array of CustomNode objects or a canvas summary string
 * @param edges - An array of Edge objects (only used when nodesOrSummary is an array)
 * @returns An array of CanvasUpdateAction objects to delete all nodes and edges.
 */
export const generateClearCanvasActions = (
  nodesOrSummary: CustomNode[] | string,
  edges?: Edge[]
): CanvasUpdateAction[] => {
  const actions: CanvasUpdateAction[] = [];
  
  // If nodesOrSummary is a string (canvas summary), parse it
  if (typeof nodesOrSummary === 'string') {
    const canvasSummary = nodesOrSummary;
    const lines = canvasSummary.split('\n');
    
    let isParsingNodes = false;
    let isParsingEdges = false;
    
    for (const line of lines) {
      if (line.startsWith('Nodes:')) {
        isParsingNodes = true;
        isParsingEdges = false;
        continue;
      } else if (line.startsWith('Edges:')) {
        isParsingNodes = false;
        isParsingEdges = true;
        continue;
      }
      
      if (isParsingNodes && line.startsWith('- ID:')) {
        // Extract node ID from line like "- ID: node-123, Type: source, Label: Website"
        const idMatch = line.match(/- ID: ([^,]+)/);
        if (idMatch && idMatch[1]) {
          actions.push({
            action: 'deleteNode',
            payload: { id: idMatch[1].trim() }
          });
        }
      } else if (isParsingEdges && line.startsWith('- ID:')) {
        // Extract edge ID from line like "- ID: edge-123, From: node1, To: node2"
        const idMatch = line.match(/- ID: ([^,]+)/);
        if (idMatch && idMatch[1]) {
          actions.push({
            action: 'deleteEdge',
            payload: { id: idMatch[1].trim() }
          });
        }
      }
    }
  } else {
    // If nodesOrSummary is an array of nodes, process directly
    const nodes = nodesOrSummary;
    
    // Add actions to delete all nodes
    nodes.forEach(node => {
      actions.push({
        action: 'deleteNode',
        payload: { id: node.id }
      });
    });
    
    // Add actions to delete all edges
    if (edges) {
      edges.forEach(edge => {
        actions.push({
          action: 'deleteEdge',
          payload: { id: edge.id }
        });
      });
    }
  }
  
  return actions;
};
